/**
 * Role-based navigation utilities
 * Centralizes the logic for determining which routes are accessible based on user role
 */

// Helper function to normalize role string (uppercase, remove spaces)
export const normalizeRole = (role: string | undefined): string => {
  if (!role) return '';
  return role.toUpperCase().replace(/\s+/g, '');
};

// Roles that should NOT see the dashboard (normalized - uppercase, no spaces)
// Includes variations: VENDEDOR, SELLER, SALES PERSON, CUSTOMER B2B
export const RESTRICTED_DASHBOARD_ROLES = ['VENDEDOR', 'SELLER', 'SALESPERSON', 'CUSTOMERB2B'];

/**
 * Get the default route for a user based on their role
 * - VENDEDOR and CUSTOMER B2B go to /home/main (Facturas Activas)
 * - All other roles go to /dashboard
 */
export const getDefaultRouteForRole = (role: string | undefined): string => {
  if (!role) return '/dashboard';
  const normalizedRole = normalizeRole(role);
  if (RESTRICTED_DASHBOARD_ROLES.includes(normalizedRole)) {
    return '/home/main'; // Facturas Activas
  }
  return '/dashboard';
};

/**
 * Check if a user with the given role can access the dashboard
 */
export const canAccessDashboard = (role: string | undefined): boolean => {
  if (!role) return true;
  const normalizedRole = normalizeRole(role);
  return !RESTRICTED_DASHBOARD_ROLES.includes(normalizedRole);
};

/**
 * Get the appropriate "home" route for a user based on their role
 * This is useful for "back to home" or "cancel" buttons
 */
export const getHomeRouteForRole = (role: string | undefined): string => {
  return getDefaultRouteForRole(role);
};

/**
 * Routes allowed for restricted roles (VENDEDOR / CUSTOMER B2B).
 * If a role is NOT listed here, it means it has broader access handled below.
 */
/**
 * Routes allowed for VENDEDOR / CUSTOMER B2B (whitelist approach).
 * These roles can ONLY access these routes — everything else is blocked.
 */
const SELLER_ALLOWED_ROUTES = ['/home/main', '/home/minor', '/invoices', '/pages/invoice/', '/customers', '/products', '/faq', '/manual'];

export const ROLE_ALLOWED_ROUTES: Record<string, string[]> = {
  VENDEDOR: SELLER_ALLOWED_ROUTES,
  SELLER: SELLER_ALLOWED_ROUTES,
  SALESPERSON: SELLER_ALLOWED_ROUTES,
  CUSTOMERB2B: SELLER_ALLOWED_ROUTES,
};

/**
 * Routes restricted from COMPANY ADMIN.
 * Only ADMINISTRATOR and SUPERADMIN can access these.
 */
export const ADMIN_ONLY_ROUTES = ['/subscriptions/plans'];

/**
 * Routes restricted to SUPERADMIN only — ni ADMINISTRATOR ni COMPANY ADMIN pueden
 * acceder, ni siquiera tecleando la URL directamente (paridad con la policy real
 * de servidor, ver ANALISIS-REPLICA-DevTools-ApiKeys.md sección 2.2).
 */
export const SUPERADMIN_ONLY_ROUTES = ['/admin/devtools'];

/**
 * Check if a user with the given role can access a specific route path.
 */
export const canAccessRoute = (role: string | undefined, path: string): boolean => {
  if (!role) return true;
  const normalized = normalizeRole(role);

  // VENDEDOR / CUSTOMER B2B: only allowed explicit routes
  const allowedRoutes = ROLE_ALLOWED_ROUTES[normalized];
  if (allowedRoutes) {
    return allowedRoutes.some(route => path.startsWith(route));
  }

  if (normalized !== 'SUPERADMIN' && SUPERADMIN_ONLY_ROUTES.some(route => path.startsWith(route))) {
    return false;
  }

  // COMPANY ADMIN / NAOS ADMINISTRATOR: everything except Empresa + Administración sections
  // (paridad: "Naos administrator" tenía el mismo nivel que Company Admin en el portal original)
  if (normalized === 'COMPANYADMIN' || normalized === 'NAOSADMINISTRATOR') {
    return !ADMIN_ONLY_ROUTES.some(route => path.startsWith(route));
  }

  // ADMINISTRATOR, SUPERADMIN: full access
  return true;
};
